import { IS_CLINIC } from "../platform/config";
import {
  Box,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import React, { useRef, useEffect, useState } from "react";
import "../css/invoicePdf.print.css";
import CustomLabel from "../components/CustomLabel";
import CustomButton from "../components/CustomButton";
import { generateInvoicePDF } from "../utils/pdfHelper";
import { COLORS } from "../assets/theme";
import { getInvoiceById } from "../services/InvoicesService";
import {getByIdBranch} from "../services/BranchesService"

const pickString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const formatDisplayText = (value: string): string => {
  if (!value) return value;
  const isAllLower = value === value.toLowerCase();
  const isAllUpper = value === value.toUpperCase();
  if (isAllLower || isAllUpper) {
    return value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return value;
};

const normalizeNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value: number): string => {
  const isNegative = value < 0;
  const absoluteValue = Math.abs(value);
  const formatted = `$${absoluteValue.toFixed(2)}`;
  return isNegative ? `-${formatted}` : formatted;
};

const formatPercentage = (value: number): string => {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const resolveLineDiscountPercent = (
  quantity: number,
  unitPrice: number,
  totalPrice: number,
  item: any
): number => {
  const explicitPercent =
    item?.discountPercent ??
    item?.discount_percent ??
    item?.metadata?.discountPercent ??
    item?.metadata?.discount_percent;
  const explicitNumeric = Number(explicitPercent);
  if (
    explicitPercent !== undefined &&
    explicitPercent !== null &&
    explicitPercent !== "" &&
    Number.isFinite(explicitNumeric)
  ) {
    return Math.min(100, Math.max(0, explicitNumeric));
  }

  const subtotal = (Number(quantity) || 0) * (Number(unitPrice) || 0);
  if (subtotal <= 0 || !Number.isFinite(totalPrice)) return 0;

  return Math.min(
    100,
    Math.max(0, ((subtotal - Math.max(0, totalPrice)) / subtotal) * 100)
  );
};

const resolveStatusText = (data: any): string => {
  const invoice = data?.invoice ?? {};

  const statusIdRaw =
    invoice?.status_id ?? data?.status_id ?? invoice?.status?.id;
  const numericStatusId = Number(statusIdRaw);
  if (Number.isFinite(numericStatusId)) {
    const fallbackStatusMap: Record<number, string> = {
      1: "Paid",
      2: "Unpaid",
      3: "Cancelled",
    };
    const mapped = fallbackStatusMap[numericStatusId];
    if (mapped) return mapped;
  }

  const statusCandidate =
    pickString(
      data?.status,
      invoice?.status,
      invoice?.status_name,
      invoice?.status_label
    ) ||
    pickString(
      invoice?.status?.name,
      invoice?.status?.label,
      invoice?.status?.title
    );

  if (statusCandidate) {
    const normalized = statusCandidate.trim().toLowerCase();
    if (normalized === "paid") return "Paid";
    if (normalized === "unpaid") return "Unpaid";
    if (normalized === "cancelled" || normalized === "cancelled") {
      return "Cancelled";
    }
    if (normalized === "pending") return "Unpaid";
    return formatDisplayText(statusCandidate);
  }

  if (typeof statusIdRaw === "string" && statusIdRaw.trim()) {
    return formatDisplayText(statusIdRaw.trim());
  }

  return "Unpaid";
};

const InvoicePdf: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const handleSendEmail = () => {
    window.location.href = `mailto:${email}`;
  };
  const handlePrintA4 = () => {
    const originalTitle = document.title;
    document.title = "  ";
    window.print();
    setTimeout(() => {
      document.title = originalTitle;
    }, 1000);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [name, setName] = useState("John Doe");
  const [email, setEmail] = useState("john.doe@gmail.com");
  const [phone, setPhone] = useState("+1234567890");

  const [invoiceNumber, setInvoiceNumber] = useState("INV-001");
  const [invoiceDate, setInvoiceDate] = useState("2023-10-01");
  const [status, setStatus] = useState("Paid");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [branchAddress, setBranchAddress] = useState<string>("");
  const [branchContact, setBranchContact] = useState<string>("");

  const invoiceRef = useRef<HTMLDivElement>(null);

  const invoiceLineItems = React.useMemo(() => {
    const invoiceItems = Array.isArray(invoiceData?.invoice?.items)
      ? invoiceData.invoice.items
      : Array.isArray(invoiceData?.items)
      ? invoiceData.items
      : [];

    if (invoiceItems.length > 0) {
      return invoiceItems
        .map((item: any) => {
          const type = String(item?.metadata?.type || item?.type || "SERVICE")
            .trim()
            .toUpperCase();
          const quantity = normalizeNumber(item?.qty ?? item?.quantity) || 1;
          const unitPrice = normalizeNumber(
            item?.unitPrice ?? item?.unit_price ?? item?.amount ?? item?.price
          );
          const totalPrice = normalizeNumber(
            item?.totalPrice ??
              item?.total_price ??
              item?.net_amount ??
              item?.netAmount
          );

          return {
            id: item?.id,
            type,
            title: item?.description || item?.name || "Service",
            quantity,
            amount: unitPrice,
            discount: resolveLineDiscountPercent(
              quantity,
              unitPrice,
              totalPrice,
              item
            ),
            net_amount: totalPrice,
          };
        });
    }

    return [];
  }, [invoiceData]);

  const renderedDetails = React.useMemo(() => {
    const serviceItems = invoiceLineItems.filter(
      (item: any) => item.type !== "PRODUCT"
    );
    if (serviceItems.length > 0) {
      return serviceItems;
    }

    if (invoiceData?.details && invoiceData.details.length > 0) {
      return invoiceData.details;
    }
    if (invoiceData?.service) {
      return [
        {
          id: invoiceData.service.id,
          title: invoiceData.service.name,
          quantity: 1,
          amount: invoiceData.service.price,
          discount: 0,
          net_amount: invoiceData.service.price,
        },
      ];
    }
    return [];
  }, [invoiceData, invoiceLineItems]);

  const renderedProducts = React.useMemo(() => {
    const productItems = invoiceLineItems.filter(
      (item: any) => item.type === "PRODUCT"
    );
    if (productItems.length > 0) {
      return productItems;
    }

    const products = Array.isArray(invoiceData?.products)
      ? invoiceData.products
      : Array.isArray(invoiceData?.metadata?.products)
      ? invoiceData.metadata.products
      : Array.isArray(invoiceData?.invoice?.metadata?.products)
      ? invoiceData.invoice.metadata.products
      : [];

    return products.map((product: any) => {
      const quantity = normalizeNumber(product?.qty ?? product?.quantity) || 1;
      const unitPrice = normalizeNumber(
        product?.unitPrice ?? product?.unit_price ?? product?.price
      );
      const totalPrice = normalizeNumber(
        product?.totalPrice ?? product?.total_price ?? product?.total
      ) || quantity * unitPrice;

      return {
        id: product?.id ?? product?.productId ?? product?.product_id,
        title:
          product?.description ||
          product?.name ||
          product?.productName ||
          "Product",
        quantity,
        amount: unitPrice,
        discount: resolveLineDiscountPercent(
          quantity,
          unitPrice,
          totalPrice,
          product
        ),
        net_amount: totalPrice,
      };
    });
  }, [invoiceData, invoiceLineItems]);

  const summary = React.useMemo(() => {
    const invoice = invoiceData?.invoice ?? {};
    const hasInvoiceItems = invoiceLineItems.length > 0;
    const subtotalValue = hasInvoiceItems
      ? invoiceLineItems.reduce(
          (sum: number, item: any) =>
            sum + normalizeNumber(item?.net_amount ?? item?.totalPrice),
          0
        )
      : normalizeNumber((invoice as any).total_amount) ||
        normalizeNumber((invoice as any).amount) ||
        normalizeNumber((invoiceData as any)?.service?.price);
    const rawDiscountPercent = hasInvoiceItems
      ? 0
      : normalizeNumber((invoice as any).discount);
    const discountAmountFromInvoice = hasInvoiceItems
      ? 0
      : normalizeNumber((invoice as any).discount_amount);
    const percentBasedDiscount =
      subtotalValue * (Math.max(0, rawDiscountPercent) / 100);
    const calculatedDiscount = discountAmountFromInvoice
      ? discountAmountFromInvoice
      : percentBasedDiscount;
    const discountAmount = Math.min(
      subtotalValue,
      Math.max(0, calculatedDiscount)
    );
    const discountPercent =
      subtotalValue > 0
        ? (discountAmount / subtotalValue) * 100
        : Math.max(0, rawDiscountPercent);
    const subtotalAfterDiscount = Math.max(0, subtotalValue - discountAmount);
    const vatPercentValue = normalizeNumber((invoice as any).Vat);
    const netAmountFromInvoice = hasInvoiceItems
      ? 0
      : normalizeNumber((invoice as any).net_amount);
    const vatAmountFromInvoice = normalizeNumber(
      (invoice as any).vat_amount ?? (invoice as any).Vat_amount
    );
    const vatAmountFromNet =
      netAmountFromInvoice > 0
        ? netAmountFromInvoice - subtotalAfterDiscount
        : 0;
    const vatAmountFromPercent =
      subtotalAfterDiscount * (vatPercentValue / 100);
    const vatAmount = (() => {
      if (!hasInvoiceItems && vatAmountFromInvoice > 0) return vatAmountFromInvoice;
      if (vatAmountFromNet > 0) return vatAmountFromNet;
      if (vatAmountFromPercent > 0) return vatAmountFromPercent;
      return 0;
    })();
    const netAmount =
      netAmountFromInvoice > 0
        ? netAmountFromInvoice
        : subtotalAfterDiscount + vatAmount;

    return {
      subtotal: subtotalValue,
      discountPercent,
      discountAmount,
      subtotalAfterDiscount,
      vatPercent: vatPercentValue,
      vatAmount,
      netAmount,
    };
  }, [invoiceData, invoiceLineItems]);

  const {
    subtotal,
    discountPercent,
    discountAmount,
    vatPercent,
    vatAmount,
    netAmount,
  } = summary;

  const discountLabel =
    discountPercent > 0
      ? `Discount (${formatPercentage(discountPercent)}%)`
      : "Discount";
  const vatLabel =
    vatPercent > 0 ? `VAT (${formatPercentage(vatPercent)}%)` : "VAT";

  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (id) {
        try {
          setLoading(true);
          const response = await getInvoiceById(id);
          const data = response?.data || response;

          setInvoiceData(data);

          if (data) {
            const invoice = data.invoice;
            const client = data.client;
            const unitPhone = pickString(
              data?.unit?.phone,
              data?.unit?.mobile,
              data?.unit?.contact_number,
              data?.unit?.phone_number
            );

            const resolvedInvoiceNumber = invoice?.id ?? invoice?.Invoice_number ?? invoice?.invoice_number ?? id;
            setInvoiceNumber(
              resolvedInvoiceNumber ? String(resolvedInvoiceNumber) : String(id)
            );

            const createdAtRaw = invoice?.createdAt || invoice?.created_at;
            setInvoiceDate(
              createdAtRaw
                ? new Date(createdAtRaw).toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0]
            );

            const statusRaw = invoice?.status || data?.status;
            setStatus(statusRaw ? formatDisplayText(String(statusRaw)) : resolveStatusText(data));
            setPaymentMethod("Cash");

            const resolvedName =
              pickString(client?.full_name, client?.name, client?.client_name) ||
              `Customer ${id}`;
            setName(resolvedName);

            const resolvedEmail =
              pickString(client?.user?.email, client?.email, data?.email) ||
              `customer${id}@example.com`;
            setEmail(resolvedEmail);

            const phoneCandidate = pickString(client?.phone, client?.mobile);
            setPhone(phoneCandidate || `+123-456-${id.padStart(4, "0")}`);
            if (unitPhone) {
              setBranchContact(unitPhone);
            }

            const branchId =
              data?.unit?.id ||
              data?.business?.id ||
              invoice?.business_id;
            if (branchId) {
              try {
                console.log('b id : ' , branchId)
                const branchResponse = await getByIdBranch(branchId);
                const branchData = branchResponse?.data || branchResponse;
                console.log("Branch data:", branchData);
                const address = branchData?.business?.address || branchData?.address || "N/A";
                setBranchAddress(address);
                console.log("Branch address:", address);
                const contact =
                  pickString(
                    branchData?.unit?.phone,
                    branchData?.unit?.mobile,
                    branchData?.phone,
                    branchData?.mobile,
                    branchData?.telephone,
                    branchData?.business?.phone,
                    branchData?.business?.mobile
                  ) || unitPhone || "N/A";
                setBranchContact(contact);
                console.log("Branch contact:", contact);
              } catch (error) {
                console.error("Error fetching branch data:", error);
                setBranchAddress("N/A");
                setBranchContact(unitPhone || "N/A");
              }
            }
          }
        } catch (error) {
          console.error("Error fetching invoice data:", error);
          setInvoiceNumber(`INV-${id.padStart(3, "0")}`);
          setInvoiceDate(new Date().toISOString().split("T")[0]);
          setName(`Customer ${id}`);
          setEmail(`customer${id}@example.com`);
          setPhone(`+123-456-${id.padStart(4, "0")}`);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchInvoiceData();
  }, [id]);

  const handleDownloadPDF = () => {
    generateInvoicePDF({
      name,
      email,
      phone,
      invoiceNumber,
      invoiceDate,
      status,
      paymentMethod,
      imageSrc: "/anyimage.png",
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: isMobile ? 1 : 3,
        py: isMobile ? 1 : 2,
        "& .MuiTableContainer-root": {
          width: "100%",
          overflowX: isMobile ? "auto" : "visible",
        },
        "& .MuiTable-root": {
          minWidth: isMobile ? 350 : 700,
        },
        "& .MuiStack-root": {
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "flex-end",
        },
        "& .MuiBox-root.invoice-image-box": {
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "center" : "flex-start",
        },
        "& .MuiBox-root.invoice-img": {
          width: isMobile ? 70 : 100,
          height: isMobile ? 70 : 100,
          marginRight: isMobile ? 0 : 2,
          marginBottom: isMobile ? 1 : 0,
        },
      }}
    >
      <Box>
        {/* Title */}
        <CustomLabel
          fontWeight={200}
          text="View Invoice"
          variant="h4"
          sx={{
            mt: 3,
            ml: 2,
            textAlign: { xs: "center", sm: "left" },
            display: "block",
            mx: { xs: "auto", sm: 0 },
          }}
        />
      </Box>

      <Box>
        {/* Buttons */}
        <Stack
          display={"flex"}
          justifyContent={{ xs: "center", sm: "flex-end" }}
          alignItems={{ xs: "center", sm: "flex-end" }}
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            mt: 2,
            ml: { xs: 0, sm: 2 },
            width: { xs: "100%", sm: "auto" },
            "& > *": {
              mb: { xs: 1.5, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              minWidth: { sm: 36 },
              minHeight: 36,
              maxHeight: 36,
              marginRight: { xs: 0, sm: 0 },
            },
          }}
        >
          <CustomButton
            label="Print A4"
            variant="contained"
            borderRadius={5}
            sx={{ mt: 2, ml: 2, minWidth: 140, minHeight: 36 }}
            onClick={handlePrintA4}
          />

          <CustomButton
            label="Print POS"
            variant="contained"
            borderRadius={5}
            sx={{ mt: 2, ml: 2, minWidth: 140, minHeight: 36 }}
          />

          <CustomButton
            label="Download PDF"
            variant="contained"
            borderRadius={5}
            sx={{ mt: 2, ml: 2, minWidth: 140, minHeight: 36 }}
            onClick={handleDownloadPDF}
          />

          <CustomButton
            label="Email"
            variant="contained"
            borderRadius={5}
            sx={{ mt: 2, ml: 2, minWidth: 140, minHeight: 36 }}
            onClick={handleSendEmail}
          />
        </Stack>
      </Box>
      {/* pdf Content */}
      <Box className="invoice-content" p={1} ref={invoiceRef}>
        {/* Invoice Image  */}
        <Box sx={{ mt: 3, ml: 2 }}>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Box
            className="invoice-image-box"
            display="flex"
            justifyContent={"space-between"}
          >
            <Box
              className="invoice-img"
              component="img"
              src="/anyimage.png"
              alt="Invoice image..."
              sx={{
                width: 100,
                height: 100,
                borderRadius: 2,
                marginRight: 2,
              }}
            />
            <CustomLabel
              text="INVOICE"
              variant="h4"
              mr={2}
              sx={{ color: "black", fontFamily: "inherit" }}
            />
          </Box>
          <Divider sx={{ mb: 3, mt: 3 }} />
        </Box>

        {/* data written before the table */}
        <Box ml={2}>
          <CustomLabel text="Billed To:" variant="h6" />
        </Box>
        <Box sx={{ mt: 3, ml: 2 }}>
          <Grid
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            container
            spacing={2}
          >
            <Box display="grid" sx={{ alignItems: "flex-start" }}>
              <CustomLabel text={`Email: ${email}`} variant="body1" />

              <CustomLabel text={`Name: ${name}`} variant="body1" />
              <CustomLabel text={`Phone: ${phone}`} variant="body1" />
            </Box>
            <Box display={"grid"} sx={{ alignItems: "flex-end" }}>
              <CustomLabel
                text={`Invoice #: ${invoiceNumber}`}
                variant="body1"
              />
              <CustomLabel
                text={`Invoice Date: ${invoiceDate}`}
                variant="body1"
              />
              <CustomLabel text={`Status: ${status}`} variant="body1" />
              <CustomLabel
                text={`Payment Method: ${paymentMethod}`}
                variant="body1"
              />
            </Box>
          </Grid>
        </Box>
        <Divider sx={{ mb: 3, mt: 1 }} />
        {/* Table */}

        <Box>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 700 }} aria-label="invoice table">
              <TableHead>
                <TableRow sx={{ backgroundColor: COLORS.MainColor }}>
                  <TableCell sx={{ color: "white" }}>Service</TableCell>
                  <TableCell align="right" sx={{ color: "white" }}>
                    Qty
                  </TableCell>
                  <TableCell align="right" sx={{ color: "white" }}>
                    Unit Cost
                  </TableCell>
                  <TableCell align="right" sx={{ color: "white" }}>
                    Discount
                  </TableCell>
                  <TableCell align="right" sx={{ color: "white" }}>
                    Price
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {/* Service Details Rows */}
                {renderedDetails.length > 0 ? (
                  renderedDetails.map((detail: any, index: number) => (
                    <TableRow key={detail.id || index}>
                      <TableCell>{detail.title || detail.name || "Service"}</TableCell>
                      <TableCell align="right">
                        {detail.quantity || 1}
                      </TableCell>
                      <TableCell align="right">
                        ${Number(detail.amount ?? detail.price ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {`${formatPercentage(
                          normalizeNumber(detail.discount || 0)
                        )}%`}
                      </TableCell>
                      <TableCell align="right">
                        ${Number(detail.net_amount ?? detail.amount ?? detail.price ?? 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell>No services</TableCell>
                    <TableCell align="right">0</TableCell>
                    <TableCell align="right">$0.00</TableCell>
                    <TableCell align="right">$0.00</TableCell>
                    <TableCell align="right">$0.00</TableCell>
                  </TableRow>
                )}

                {renderedProducts.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ color: "white", backgroundColor: COLORS.MainColor }}
                      >
                        Products
                      </TableCell>
                    </TableRow>
                    {renderedProducts.map((product: any, index: number) => (
                      <TableRow key={product.id || `product-${index}`}>
                        <TableCell>
                          {product.title || product.name || "Product"}
                        </TableCell>
                        <TableCell align="right">
                          {product.quantity || 1}
                        </TableCell>
                        <TableCell align="right">
                          ${Number(product.amount ?? product.price ?? 0).toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {`${formatPercentage(
                            normalizeNumber(product.discount || 0)
                          )}%`}
                        </TableCell>
                        <TableCell align="right">
                          ${Number(
                            product.net_amount ??
                              product.amount ??
                              product.price ??
                              0
                          ).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {/* Business/Court Name Row */}
                <TableRow>
                  <TableCell>
                    {IS_CLINIC ? "Booked with" : "Court Name"}: {invoiceData?.unit?.name || "N/A"}
                  </TableCell>
                  <TableCell />
                  <TableCell sx={{ backgroundColor: "#e0e0e0" }}>
                    Sub Total
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{ backgroundColor: "#e0e0e0" }}
                    align="right"
                  >
                    {formatCurrency(subtotal)}
                  </TableCell>
                </TableRow>

                {/* Discount Row */}
                {invoiceData?.invoice && (
                  <TableRow>
                    <TableCell>
                      {" "}
                      <Box>Address: {branchAddress || "N/A"}</Box>
                    </TableCell>
                    <TableCell />
                    <TableCell sx={{ backgroundColor: "#e0e0e0" }}>
                      {discountLabel}
                    </TableCell>
                    <TableCell
                      colSpan={2}
                      sx={{ backgroundColor: "#e0e0e0" }}
                      align="right"
                    >
                      {formatCurrency(-discountAmount)}
                    </TableCell>
                  </TableRow>
                )}

                {/* VAT Row */}
                <TableRow>
                  <TableCell>Contact: {branchContact}</TableCell>
                  <TableCell />
                  <TableCell sx={{ backgroundColor: "#e0e0e0" }}>
                    {vatLabel}
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{ backgroundColor: "#e0e0e0" }}
                    align="right"
                  >
                    {formatCurrency(vatAmount)}
                  </TableCell>
                </TableRow>

                {/* Total Row */}
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell />
                  <TableCell
                    sx={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    colSpan={2}
                    sx={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}
                    align="right"
                  >
                    {formatCurrency(netAmount)}
                  </TableCell>
                </TableRow>

                {/* Notes Row */}
                {invoiceData?.invoice?.notes && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      Notes: {invoiceData.invoice.notes}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoicePdf;
